/**
 * Action: update_request_status
 *
 * Change a request's status. The first action in the catalog that
 * pairs naturally with the focused-request context shipped in
 * #1747 + #1784 — when a user on RequestDetail says "mark this
 * resolved", the planner has `focusedRequestId` and the proposal
 * lands with the right request_id automatically.
 *
 * Inverse
 * -------
 * Symmetric. We fetch the current status BEFORE updating so we can
 * carry it forward as the inverse params. One click of "Undo:
 * restore status" rolls back to the prior state.
 *
 * Defense in depth
 * ----------------
 * - The status enum is enforced both client-side (Zod) and at the
 *   DB level (CHECK constraint on `requests.status`).
 * - We never trust the planner's `request_id` blindly — the
 *   preview fetch validates the request exists in the current
 *   organization, surfacing a `precondition_failed` rejection
 *   otherwise.
 * - Audit row records both `previous_status` and `new_status` so
 *   admin investigations can reconstruct the chain.
 *
 * Out of scope (sibling iterations)
 * ---------------------------------
 * - Recipient notifications: the existing `useUpdateRequestStatus`
 *   hook inserts a `notifications` row for the requester after a
 *   status change. This action skips that path — a notification on
 *   every Solomon-initiated change felt noisy. Add a `notify` boolean
 *   to params if/when desired.
 * - Recipient notifications are still out of scope (see above), but
 *   status→timestamp bookkeeping now mirrors the canonical request
 *   update path so resolved/closed transitions keep analytics sane.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

// Mirror of the canonical `RequestStatus` type in src/types/requests.ts.
// Duplicated here rather than imported because the engine should not
// pull in module-level constants from arbitrary feature areas — the
// action engine's contract is JSON-shaped params, not Forge types.
const REQUEST_STATUSES = [
  "open",
  "in_progress",
  "waiting",
  "resolved",
  "closed",
] as const;

const updateRequestStatusParams = z.object({
  request_id: z.string().guid(),
  status: z.enum(REQUEST_STATUSES),
  /**
   * Optional freeform note. Stored on the audit row's `newData.note`
   * so reviewers can see "why was this resolved?". Not written to the
   * request row itself.
   */
  note: z.string().trim().max(2000).optional().nullable(),
});

type UpdateRequestStatusParams = z.infer<typeof updateRequestStatusParams>;

const STATUS_LABELS: Record<(typeof REQUEST_STATUSES)[number], string> = {
  open: "Open",
  in_progress: "In Progress",
  waiting: "Waiting",
  resolved: "Resolved",
  closed: "Closed",
};

interface ResolvedRequest {
  id: string;
  title: string;
  status: (typeof REQUEST_STATUSES)[number];
}

async function resolveRequest(
  organizationId: string,
  requestId: string,
): Promise<ResolvedRequest> {
  const { data, error } = await supabase
    .from("requests")
    .select("id, summary, status")
    .eq("organization_id", organizationId)
    .eq("id", requestId)
    .maybeSingle();

  if (error || !data) {
    // Treat as a precondition_failure surfaced via thrown Error;
    // prepareAction converts to a rejection card the UI renders.
    throw new Error("Request not found in this organization.");
  }

  const status = data.status as ResolvedRequest["status"];
  if (!REQUEST_STATUSES.includes(status)) {
    // Defensive: the DB CHECK constraint should make this impossible,
    // but if a future migration adds a status not in our enum, we
    // surface it as a precondition_failed rather than a runtime
    // crash inside the executor.
    throw new Error(
      `Request has unknown status "${status}" — refusing to overwrite.`,
    );
  }

  return {
    id: data.id,
    title: data.summary ?? "this request",
    status,
  };
}

const updateRequestStatusDefinition: ActionDefinition<UpdateRequestStatusParams> = {
  id: "update_request_status",
  label: "Update request status",
  description: "Change the status of a request (open / in_progress / waiting / resolved / closed).",
  requiredTier: "staff",
  paramsSchema: updateRequestStatusParams,
  // Status changes ripple to dashboards, attention widgets, and (in
  // the legacy useUpdateRequestStatus path) notifications. Default
  // to confirm so the user sees the diff before committing.
  defaultRequiresConfirm: true,
  async buildPreview(params, ctx) {
    const request = await resolveRequest(ctx.organizationId, params.request_id);
    if (request.status === params.status) {
      // No-op transition. Surface a rejection-ish preview so the
      // user sees "this request is already X" rather than a
      // confirm button that does nothing.
      throw new Error(
        `Request "${request.title}" is already ${STATUS_LABELS[params.status]}.`,
      );
    }
    return {
      summary: `Mark "${request.title}" as ${STATUS_LABELS[params.status]} (currently ${STATUS_LABELS[request.status]})`,
    };
  },
  async execute(params, ctx): Promise<ActionResult> {
    // Re-resolve at execute time so the inverse captures the
    // CURRENT status, not whatever buildPreview saw. The user may
    // have changed it via another surface between preview and
    // confirm — capturing the prior state at execute is what makes
    // the inverse honest.
    let request: ResolvedRequest;
    try {
      request = await resolveRequest(ctx.organizationId, params.request_id);
    } catch (err) {
      return {
        ok: false,
        summary: "Could not load request",
        error: err instanceof Error ? err.message : String(err),
      };
    }

    if (request.status === params.status) {
      // Idempotent: nothing to do. Treat as success so the toast
      // closes cleanly. Don't write an audit row for a no-op.
      return {
        ok: true,
        summary: `"${request.title}" is already ${STATUS_LABELS[params.status]}`,
        inverse: null,
      };
    }

    const { error } = await supabase
      .from("requests")
      .update({
        status: params.status,
        ...(params.status === "resolved" ? { resolved_at: new Date().toISOString() } : {}),
        ...(params.status === "closed" ? { closed_at: new Date().toISOString() } : {}),
      })
      .eq("organization_id", ctx.organizationId)
      .eq("id", params.request_id);

    if (error) {
      return {
        ok: false,
        summary: `Failed to update "${request.title}"`,
        error: error.message,
      };
    }

    const audit = await logAudit({
      action: "request.status_update",
      tableName: "requests",
      recordId: params.request_id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      oldData: { status: request.status },
      newData: {
        status: params.status,
        previous_status: request.status,
        title: request.title,
        note: params.note ?? null,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    return {
      ok: true,
      summary: `Marked "${request.title}" as ${STATUS_LABELS[params.status]}`,
      auditLogId: audit?.id ?? null,
      inverse: {
        actionId: "update_request_status",
        params: {
          request_id: params.request_id,
          status: request.status,
        },
        label: `Undo: restore status to ${STATUS_LABELS[request.status]}`,
      },
    };
  },
};

registerAction(updateRequestStatusDefinition);

export { updateRequestStatusDefinition };
